import { hasInjectionContext, inject, type App, type InjectionKey } from 'vue'
import type { ApiClient } from './client'

/**
 * How components and stores get hold of the API client.
 *
 * Two mechanisms, on purpose:
 *
 * - **Injection**, for components. `provideApiClient(app, client)` at bootstrap, `useApiClient()`
 *   in a `setup()`. This is the ordinary Vue way and it keeps the client out of module scope.
 * - **A registered default**, for Pinia stores and for tests. A store's setup does not always run
 *   inside a component's injection context, and a test that wants a stub client should not have
 *   to mount an application to install one.
 *
 * What is deliberately *not* here is a client constructed at module load. That would be a session
 * constructed at module load: every test would share one token and one in-flight refresh, and the
 * first test to log in would decide what the others saw.
 */
export const API_CLIENT: InjectionKey<ApiClient> = Symbol('oui-observe:api-client')

let registered: ApiClient | null = null

/** Install the client for stores and for anything outside an injection context. */
export const setApiClient = (client: ApiClient | null): void => {
  registered = client
}

export const provideApiClient = (app: App, client: ApiClient): void => {
  app.provide(API_CLIENT, client)
  setApiClient(client)
}

/**
 * The client for the current context.
 *
 * Prefers the injected one so a test can mount a subtree with its own; falls back to the
 * registered default. Throws rather than returning null: a component that silently renders
 * without a client would look like an empty screen instead of a misconfiguration.
 */
export const useApiClient = (): ApiClient => {
  // Guarded, because a Pinia store created outside an application — which is exactly how the
  // tests create them — has no injection context, and calling `inject` there is a Vue warning
  // rather than an error. Checking first keeps a clean test run clean, so a real warning is
  // still worth reading when one appears.
  const injected = hasInjectionContext() ? inject(API_CLIENT, null) : null
  const client = injected ?? registered

  if (!client) {
    throw new Error(
      'No API client available. Call provideApiClient(app, client) during bootstrap, or ' +
        'setApiClient(client) in a test setup file.',
    )
  }

  return client
}
